import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  borrarMeta,
  guardarMeta,
  obtenerCapitulos,
  type Manga,
  type MangaDetalle,
} from '../api/consultas'
import Hoja from './Hoja'
import { comoDia } from '../lib/fechas'
import {
  CLAVE_META,
  conMeta,
  fechaDeFinal,
  resumirLectura,
  type Finalizada,
  type Veredicto,
} from '../lib/finalizadas'

/**
 * Guardar cambios de una serie ya finalizada (puntuacion y nota).
 *
 * Escribe antes en la copia local de la biblioteca y despues en el VPS: una
 * estrella que tarda en pintarse medio segundo se siente rota, y aqui no hay
 * nada que se pueda perder si el viaje falla —al recargar vuelve lo que haya
 * en el servidor—.
 */
export function useGuardarFinalizada() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: ({ mangaId, datos }: { mangaId: number; datos: Finalizada }) =>
      guardarMeta(mangaId, CLAVE_META, JSON.stringify(datos)),
    onMutate: ({ mangaId, datos }) => {
      const valor = JSON.stringify(datos)
      clienteConsultas.setQueryData<Manga[]>(['biblioteca'], (previo) =>
        previo?.map((m) => (m.id === mangaId ? { ...m, meta: conMeta(m.meta, CLAVE_META, valor) } : m)),
      )
      clienteConsultas.setQueryData<MangaDetalle>(['manga', mangaId], (previo) =>
        previo ? { ...previo, meta: conMeta(previo.meta, CLAVE_META, valor) } : previo,
      )
    },
    onSettled: () => clienteConsultas.invalidateQueries({ queryKey: ['biblioteca'] }),
  })
}

interface Props {
  mangaId: number
  titulo: string
  finalizada: Finalizada | null
  /** En el estante se puede ademas escribir que te parecio. */
  conNota?: boolean
  alCerrar: () => void
}

/**
 * La hoja que sale al mantener pulsada una portada (o al click derecho).
 *
 * Se abre desde tres sitios —la rejilla de la biblioteca, la ficha y el
 * estante— y por eso el trabajo esta aqui y no en las pantallas.
 */
export default function Finalizar({ mangaId, titulo, finalizada, conNota, alCerrar }: Props) {
  const clienteConsultas = useQueryClient()
  const [nota, setNota] = useState(finalizada?.nota ?? '')
  const guardar = useGuardarFinalizada()

  const cambiar = useMutation({
    mutationFn: async (estado: Veredicto | null) => {
      if (!estado) {
        await borrarMeta(mangaId, CLAVE_META)
        return
      }
      /*
       * Los capitulos hacen falta para el resumen (cuantos leiste, desde
       * cuando y hasta cuando), y desde la rejilla no estan cargados. Se
       * piden por el cliente de consultas y no a pelo para reaprovechar los
       * que la ficha ya tenga: en One Piece son mil doscientas filas.
       */
      const capitulos = await clienteConsultas.fetchQuery({
        queryKey: ['capitulos', mangaId],
        queryFn: () => obtenerCapitulos(mangaId),
        staleTime: 5 * 60 * 1000,
      })
      await guardarMeta(
        mangaId,
        CLAVE_META,
        JSON.stringify(resumirLectura(capitulos, estado, finalizada)),
      )
    },
    onSuccess: () => {
      void clienteConsultas.invalidateQueries({ queryKey: ['biblioteca'] })
      void clienteConsultas.invalidateQueries({ queryKey: ['manga', mangaId] })
      alCerrar()
    },
  })

  const trabajando = cambiar.isPending

  function guardarNota() {
    if (!finalizada) return
    guardar.mutate({ mangaId, datos: { ...finalizada, nota: nota.trim() } })
    alCerrar()
  }

  return (
    <Hoja
      titulo={titulo}
      nota={
        finalizada
          ? `${finalizada.estado === 'abandonada' ? 'Abandonada' : 'Terminada'} el ${comoDia(
              fechaDeFinal(finalizada),
            )}`
          : 'Guardar esta serie en Finalizadas. Sale de la biblioteca, pero no se borra nada: el progreso y las copias siguen igual.'
      }
      alCerrar={alCerrar}
    >
      {finalizada && conNota && (
        <div className="editor-nota">
          <label htmlFor="nota-finalizada">Que te parecio</label>
          <textarea
            id="nota-finalizada"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Lo que quieras acordarte dentro de dos anos"
            rows={3}
          />
          <button className="boton" onClick={guardarNota}>
            Guardar
          </button>
        </div>
      )}

      {finalizada ? (
        <>
          <button
            className="boton secundario"
            disabled={trabajando}
            onClick={() =>
              cambiar.mutate(finalizada.estado === 'terminada' ? 'abandonada' : 'terminada')
            }
          >
            {finalizada.estado === 'terminada'
              ? '🚪 En realidad la deje a medias'
              : '✓ En realidad la termine'}
            <small>Cambia como aparece en el estante</small>
          </button>
          <button
            className="boton secundario"
            disabled={trabajando}
            onClick={() => cambiar.mutate(null)}
          >
            ↩ Devolver a la biblioteca
            <small>Vuelve a la rejilla con las demas</small>
          </button>
        </>
      ) : (
        <>
          <button
            className="boton"
            disabled={trabajando}
            onClick={() => cambiar.mutate('terminada')}
          >
            ✓ Marcar como finalizada
            <small>La leiste entera y ya esta</small>
          </button>
          <button
            className="boton secundario"
            disabled={trabajando}
            onClick={() => cambiar.mutate('abandonada')}
          >
            🚪 La deje a medias
            <small>No vas a seguirla. Tambien pasa a Finalizadas</small>
          </button>
        </>
      )}

      {cambiar.error && <p className="error-texto">{(cambiar.error as Error).message}</p>}
    </Hoja>
  )
}
